import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Crown } from "lucide-react";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useSubscription } from "@/hooks/useSubscription";
import { createPortalSession } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/pricing")({
  component: PricingPage,
  head: () => ({ meta: [{ title: "Premium · Serenity" }] }),
});

const FEATURES = [
  "Unlimited Sage AI counseling",
  "Deeper mood insights & trends",
  "Priority therapist booking",
  "Crisis-resource quick access",
  "Cancel anytime — keep access until period end",
];

function PricingPage() {
  const { data: user } = useQuery({
    queryKey: ["auth-user"],
    queryFn: async () => (await supabase.auth.getUser()).data.user,
  });
  const { subscription, isActive } = useSubscription(user?.id);
  const { openCheckout, closeCheckout, isOpen, checkoutElement } = useStripeCheckout();
  const [portalLoading, setPortalLoading] = useState(false);

  const subscribe = (priceId: "premium_monthly" | "premium_yearly") => {
    if (!user) { toast.error("Sign in first"); return; }
    openCheckout({
      kind: "price",
      priceId,
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const manageBilling = async () => {
    try {
      setPortalLoading(true);
      const result = await createPortalSession({
        data: { environment: getStripeEnvironment(), returnUrl: `${window.location.origin}/pricing` },
      });
      if ("error" in result) throw new Error(result.error);
      window.open(result.url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-3xl text-gradient flex items-center gap-2"><Crown className="h-7 w-7 text-accent" />Serenity Premium</h1>
        <p className="text-sm text-muted-foreground mt-1">Go deeper with unlimited AI counseling and richer insights.</p>
      </div>

      {isActive && subscription && (
        <div className="glass rounded-3xl p-6 space-y-3 border-primary/40">
          <div className="flex items-center gap-2 text-accent"><Sparkles className="h-5 w-5" /><span className="font-medium">Premium active</span></div>
          <div className="text-sm text-muted-foreground">
            Plan: <span className="text-foreground">{subscription.price_id}</span> · Status: <span className="text-foreground capitalize">{subscription.status}</span>
            {subscription.current_period_end && (
              <> · Renews/ends: <span className="text-foreground">{new Date(subscription.current_period_end).toLocaleDateString()}</span></>
            )}
            {subscription.cancel_at_period_end && <span className="text-amber-400"> · cancels at period end</span>}
          </div>
          <Button onClick={manageBilling} disabled={portalLoading} variant="secondary">
            {portalLoading ? "Opening..." : "Manage billing"}
          </Button>
        </div>
      )}

      {!isOpen && (
        <div className="grid md:grid-cols-2 gap-4">
          <PlanCard
            title="Monthly"
            price="$12"
            cadence="/month"
            cta={isActive ? "Switch in portal" : "Start Premium"}
            disabled={isActive}
            onClick={() => subscribe("premium_monthly")}
          />
          <PlanCard
            title="Yearly"
            price="$99"
            cadence="/year"
            badge="Save ~31%"
            cta={isActive ? "Switch in portal" : "Start Premium"}
            disabled={isActive}
            onClick={() => subscribe("premium_yearly")}
            featured
          />
        </div>
      )}

      {isOpen && (
        <div className="glass rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl">Complete payment</h2>
            <Button variant="ghost" size="sm" onClick={closeCheckout}>Close</Button>
          </div>
          {checkoutElement}
        </div>
      )}
    </div>
  );
}

function PlanCard({
  title, price, cadence, cta, onClick, disabled, badge, featured,
}: {
  title: string; price: string; cadence: string; cta: string;
  onClick: () => void; disabled?: boolean; badge?: string; featured?: boolean;
}) {
  return (
    <div className={`glass rounded-3xl p-6 space-y-5 ${featured ? "border-primary/60 glow-primary" : ""}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-xl">{title}</h3>
        {badge && <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent">{badge}</span>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-medium">{price}</span>
        <span className="text-sm text-muted-foreground">{cadence}</span>
      </div>
      <ul className="space-y-2">
        {FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onClick} disabled={disabled} className="w-full">{cta}</Button>
    </div>
  );
}
